/**
 * Кнопка «Обновить положение».
 *
 * Трекер копит плановые записи и шлёт их пачками — так дешевле по трафику, но
 * положение приходит с задержкой. Эта кнопка пробивает накопление: команда
 * заставляет трекер сделать запись прямо сейчас и отправить её высоким
 * приоритетом.
 *
 * Координат в ответе нет и быть не может. Трекер отвечает не нам, а Traccar,
 * отдельной записью — она придёт живой лентой, и карточка обновится сама.
 * Поэтому кнопка сообщает «запрошено», а не «готово»: обещать доставку, не
 * дождавшись её, — ровно та ошибка, из-за которой переделывали блокировку.
 *
 * Все ограничения — на сервере: пауза между запросами и суточный потолок
 * берегут трафик симки. Здесь их копии нет намеренно: вторая копия правила
 * однажды разошлась бы с первой, а отказ и так приходит понятным текстом.
 */

import { useEffect, useState } from 'react'

import { ApiError, api } from './api'
import { dict, type Lang } from './i18n'

/** Сколько держать надпись «запрошено», миллисекунды. */
const ASKED_MS = 8000

type Props = {
  deviceId: number
  lang: Lang
}

export function RefreshButton({ deviceId, lang }: Props) {
  const t = dict(lang)
  const [busy, setBusy] = useState(false)
  const [asked, setAsked] = useState(false)
  const [error, setError] = useState('')

  // Новая машина — новое состояние: чужое «запрошено» показывать нельзя.
  useEffect(() => {
    setAsked(false)
    setError('')
  }, [deviceId])

  useEffect(() => {
    if (!asked) return
    const timer = setTimeout(() => setAsked(false), ASKED_MS)
    return () => clearTimeout(timer)
  }, [asked])

  async function ask() {
    setBusy(true)
    setError('')
    try {
      await api.refresh(deviceId)
      setAsked(true)
    } catch (caught) {
      // Пауза, суточный потолок и машина без связи приходят готовым текстом:
      // он объясняет, когда пробовать снова.
      setError(caught instanceof ApiError && caught.message ? caught.message : t.networkError)
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <button className="act ghost" disabled={busy} onClick={() => void ask()}>
        {busy ? t.working : t.refreshAction}
      </button>
      {asked && !error && <p className="hint">{t.refreshAsked}</p>}
      {error && (
        <div className="login-error" role="alert">
          {error}
        </div>
      )}
    </>
  )
}
